import React, { useEffect, useState } from "react";
import { Box, MenuItem, Popover, TextField, Typography } from "@mui/material";
import { CasLoader } from "../brand/CasLoader";
import { useDispatch, useSelector } from "react-redux";
import { ActionType, RootState } from "../../reducer";
import { useActiveSessionId, useSessionHistory } from "../../slices/ui";
import { useDevcontainerSearch } from "../../hook/devcontainer";
import { DevcontainerOption, getDevcontainerById, getDevcontainerNameById } from "../../api/devcontainer";
import { useDefaultDevcontainerId } from "../../hook/defaultDevcontainer";
import { pickerDevcontainer } from "../../lib/devcontainerDefault";
import { useAppIdMap } from "../../hook/app";

// Debounced-typeahead devcontainer picker (established pattern — mirrors
// usePackageSearch / SearchTable). The search box drives an app-scoped Mango
// name-search (searchDevcontainers); options carry {name, devcontainer-id}. On
// select we store the devcontainer-id (session.create carries it). Locks
// (mirror ModelPicker.tsx) once a session is active — DC chosen at new-session
// creation only.
//
// Controlled mode: when `onChange` is supplied (the service-account block), the
// picker becomes controlled — `value` is the devcontainer-id, `onChange` the
// setter, and the AI-panel session lock is bypassed. Props omitted = the
// original AI-panel behavior verbatim (redux-backed, session-locked).
interface DevcontainerPickerProps {
  value?: string | null; // controlled devcontainer-id (SA block)
  onChange?: (id: string | null) => void; // controlled setter
  disabled?: boolean;
}
export const DevcontainerPicker: React.FC<DevcontainerPickerProps> = ({ value, onChange, disabled }) => {
  const controlled = onChange !== undefined;
  const dispatch = useDispatch();
  const reduxSelected = useSelector((s: RootState) => s.ui.selectedDevcontainer);
  const activeId = useActiveSessionId(); // hooks unconditional (rules of hooks)
  const sessions = useSessionHistory();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { searchString, setSearchString, isLoading, page } = useDevcontainerSearch({
    disabledSearch: !open,
  });

  const defaultId = useDefaultDevcontainerId();
  // U.15: a new chat shows the Default Devcontainer unless one was picked; the picker never writes the default into redux, so session.create
  // still omits `devcontainer` and the agent service applies the default (omega-ai-agent-service pages/agent-service/default-devcontainer.html, Entry Points).
  const selected = controlled ? (value ?? null) : pickerDevcontainer({ picked: reduxSelected, defaultId, hasActiveSession: Boolean(activeId) && sessions.some((s) => s["session-id"] === activeId) });

  // U.7: resolve the selected devcontainer's NAME so the trigger shows name + id
  // (mirroring the option rows / the service-account block) instead of the raw UUID.
  // Keyed on the selected id + appId; falls back to the id when unresolved/null.
  const { appId } = useAppIdMap();
  const [selectedName, setSelectedName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    setSelectedName(null);
    if (selected) {
      // the session's workspace first (the existing lookup), then the id alone: the default may live in another workspace (U.5).
      const inWorkspace = appId ? getDevcontainerById(appId, selected) : Promise.resolve(null);
      inWorkspace
        .then((opt) => opt ?? getDevcontainerNameById(selected))
        .then((opt) => { if (!cancelled) setSelectedName(opt?.name ?? null); })
        .catch(() => { if (!cancelled) setSelectedName(null); });
    }
    return () => { cancelled = true; };
  }, [selected, appId]);

  // When an existing chat is active, lock the picker to that chat's stored
  // devcontainer. The user cannot change devcontainers mid-conversation; a new
  // chat lifts the lock. AI-panel session lock applies ONLY in uncontrolled
  // (AI-panel) mode.
  const lockedDevcontainer = !controlled && activeId
    ? (sessions.find((s) => s["session-id"] === activeId) as any)?.devcontainer ?? reduxSelected
    : null;

  if (lockedDevcontainer) {
    return (
      <div
        className="devcontainer-picker"
        data-testid="devcontainer-locked"
        style={{ display: "flex", alignItems: "center", flex: 1, fontSize: 11, opacity: 0.7 }}
      >
        <span title="Devcontainer is fixed for an existing chat">{lockedDevcontainer}</span>
      </div>
    );
  }

  const pick = (option: DevcontainerOption | null) => {
    setAnchorEl(null);
    setSearchString("");
    const id = option ? option["devcontainer-id"] : null;
    if (controlled) onChange!(id);
    else dispatch({ type: ActionType.SET_SELECTED_DEVCONTAINER, devcontainer: id });
  };

  return (
    <>
      <Box
        data-testid="devcontainer-select"
        role="button"
        aria-haspopup="listbox"
        onClick={(e) => {
          if (!disabled) setAnchorEl(e.currentTarget);
        }}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", flex: 1, maxWidth: 200 }}
      >
        {selected ? (
          selectedName ? (
            // name (prominent) + id (caption) — mirrors the option-row layout
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {selectedName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {selected}
              </Typography>
            </Box>
          ) : (
            // name not resolved yet (or null) → fall back to the id
            <Typography noWrap sx={{ fontSize: "0.75rem" }}>
              {selected}
            </Typography>
          )
        ) : (
          <Typography noWrap sx={{ fontSize: "0.75rem" }}>
            (none)
          </Typography>
        )}
      </Box>
      <Popover anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <Box sx={{ p: 1, width: "20rem" }}>
          <TextField
            fullWidth
            size="small"
            value={searchString}
            onChange={(e) => setSearchString(e.currentTarget.value)}
            placeholder="Search devcontainers…"
            inputProps={{ "data-testid": "devcontainer-search" }}
          />
          <Box sx={{ maxHeight: "12rem", overflowY: "auto", mt: 1 }}>
            {/* U.6: match the other search modals' loading UX — while the debounced search
                is in flight, HIDE the option list and show a centered spinner, then
                repopulate when results arrive. Spinner mirrors menu/component.tsx
                (`<CasLoader size={20} />`); the !isLoading gate mirrors
                searchTable.tsx (`{!isLoading && (…rows…)}` / `{isLoading && <CasLoader/>}`). */}
            {isLoading ? (
              <Box
                data-testid="devcontainer-loading"
                sx={{ display: "flex", justifyContent: "center", p: 2 }}
              >
                <CasLoader size={20} />
              </Box>
            ) : (
              <>
                <MenuItem data-testid="devcontainer-option-none" onClick={() => pick(null)}>
                  <Typography variant="body2" color="text.secondary">
                    (none)
                  </Typography>
                </MenuItem>
                {page.map((option) => (
                  <MenuItem
                    key={option["devcontainer-id"]}
                    data-testid={`devcontainer-option-${option["devcontainer-id"]}`}
                    onClick={() => pick(option)}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option["devcontainer-id"]}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
                {page.length === 0 && searchString !== "" && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No devcontainers found
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Popover>
    </>
  );
};
